/**
 * kpop/hero frontend behaviour:
 *  - reveal the animated headline lines on load
 *  - inject the muted, looping YouTube background video
 *  - pointer-reactive 3D tilt + glare on the holographic member card
 *    (falls back to a gentle float when pointer is coarse / reduced-motion)
 */

const YT_ORIGIN = 'https://www.youtube-nocookie.com';

/** Send one iframe-API message to the player. */
function postToPlayer(frame, message) {
	frame.contentWindow?.postMessage(JSON.stringify(message), YT_ORIGIN);
}

/**
 * Drop the caption track.
 *
 * No URL parameter can do this: `cc_load_policy=0` only means "respect the
 * viewer's own setting", so anyone browsing with captions enabled gets subtitles
 * burned across the hero. Unloading the module is the only reliable answer.
 *
 * The player's title/share bar has no parameter at all — `showinfo` was removed
 * in 2018 and `modestbranding` no longer has any effect — so style.css crops
 * that off the edges with the video overscan instead.
 *
 * @param {HTMLIFrameElement} frame The player iframe.
 */
function hidePlayerChrome(frame) {
	postToPlayer(frame, { event: 'command', func: 'unloadModule', args: ['captions'] });
	postToPlayer(frame, { event: 'command', func: 'unloadModule', args: ['cc'] });
}

/**
 * Build and attach the background iframe.
 *
 * The markup ships without it so that reduced-motion and no-JS visitors are
 * never charged for a third-party embed — they simply keep the gradient
 * backdrop. `is-video-ready` fades in both the video and its overlay together,
 * so the overlay never darkens a backdrop that has no video behind it.
 *
 * @param {HTMLElement} hero The .kpop-hero section.
 */
function initHeroVideo(hero) {
	const host = hero.querySelector('[data-kpop-hero-video]');
	const id = host?.dataset.kpopHeroVideo;
	if (!id) return;

	// `loop` is ignored for a single video unless it is also the playlist.
	// `enablejsapi` is what lets hidePlayerChrome() talk to the player at all.
	const params = new URLSearchParams({
		autoplay: '1',
		mute: '1',
		controls: '0',
		loop: '1',
		playlist: id,
		playsinline: '1',
		modestbranding: '1',
		rel: '0',
		iv_load_policy: '3',
		cc_load_policy: '0',
		disablekb: '1',
		fs: '0',
		enablejsapi: '1',
		origin: window.location.origin,
	});

	const frame = document.createElement('iframe');
	frame.src = `${YT_ORIGIN}/embed/${encodeURIComponent(id)}?${params}`;
	frame.title = host.dataset.videoTitle || '';
	frame.tabIndex = -1;
	frame.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');

	// The player ignores commands until it has initialised, and it only reports
	// in once we say we are listening — so ask twice: on load, and again on the
	// player's own ready event.
	window.addEventListener('message', (event) => {
		if (event.origin !== YT_ORIGIN || event.source !== frame.contentWindow) return;

		let payload;
		try {
			payload = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
		} catch {
			return;
		}

		if (payload?.event === 'onReady' || payload?.event === 'initialDelivery') {
			hidePlayerChrome(frame);
		}
	});

	frame.addEventListener(
		'load',
		() => {
			hero.classList.add('is-video-ready');
			postToPlayer(frame, { event: 'listening' });
			hidePlayerChrome(frame);
		},
		{ once: true }
	);

	host.appendChild(frame);
}

export function initHero() {
	const heroes = document.querySelectorAll('.kpop-hero');
	if (!heroes.length) return;

	const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	heroes.forEach((hero) => {
		// Headline line reveal.
		requestAnimationFrame(() => hero.classList.add('is-loaded'));

		if (!reduce) initHeroVideo(hero);

		const card = hero.querySelector('[data-kpop-pcard]');
		if (!card) return;

		if (!reduce && window.matchMedia('(pointer:fine)').matches) {
			const stage = card.parentElement;
			let raf;
			const onMove = (ev) => {
				const r = card.getBoundingClientRect();
				const px = (ev.clientX - r.left) / r.width;
				const py = (ev.clientY - r.top) / r.height;
				cancelAnimationFrame(raf);
				raf = requestAnimationFrame(() => {
					const rx = (0.5 - py) * 22;
					const ry = (px - 0.5) * 26;
					card.style.transform = `rotateX(${rx}deg) rotateY(${ry}deg) scale(1.04)`;
					card.style.setProperty('--mx', `${px * 100}%`);
					card.style.setProperty('--my', `${py * 100}%`);
				});
			};
			stage.addEventListener('mousemove', onMove);
			stage.addEventListener('mouseleave', () => {
				card.style.transform = '';
				card.style.setProperty('--mx', '50%');
				card.style.setProperty('--my', '30%');
			});
		} else if (!reduce) {
			card.style.animation = 'kpop-foilfloat 6s ease-in-out infinite';
			if (!document.getElementById('kpop-foilfloat-kf')) {
				const s = document.createElement('style');
				s.id = 'kpop-foilfloat-kf';
				s.textContent =
					'@keyframes kpop-foilfloat{0%,100%{transform:translateY(0) rotateY(-6deg)}50%{transform:translateY(-12px) rotateY(6deg)}}';
				document.head.appendChild(s);
			}
		}
	});
}
